import type { CampaignDraftPostItem } from '@/entities/campaign';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useRef } from 'react';

import {
  emptyPostItem,
  focusElement,
  withFallbackPostRow,
} from '../../../../hooks/campaign-create-shared';

type UseCampaignPostRowsArgs = {
  postItems: CampaignDraftPostItem[];
  remainingSlots: number;
  setPostItems: Dispatch<SetStateAction<CampaignDraftPostItem[]>>;
};

export type CampaignPostRows = ReturnType<typeof useCampaignPostRows>;

export function useCampaignPostRows({
  postItems,
  remainingSlots,
  setPostItems,
}: UseCampaignPostRowsArgs) {
  const postUrlRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const add = useCallback(() => {
    const emptyRow = postItems.find((item) => item.postUrl.trim().length === 0);
    if (emptyRow) {
      focusElement(postUrlRefs.current[emptyRow.id]);
      return emptyRow.id;
    }

    if (remainingSlots === 0) return undefined;

    const newRow = emptyPostItem();
    setPostItems((items) => [...items, newRow]);
    return newRow.id;
  }, [postItems, remainingSlots, setPostItems]);

  const remove = useCallback(
    (rowId: string) => {
      setPostItems((items) => withFallbackPostRow(items.filter((item) => item.id !== rowId)));
    },
    [setPostItems],
  );

  const clear = useCallback(() => {
    setPostItems([emptyPostItem()]);
  }, [setPostItems]);

  const changeUrl = useCallback(
    (rowId: string, postUrl: string) => {
      setPostItems((items) =>
        items.map((item) => (item.id === rowId ? { ...item, postUrl } : item)),
      );
    },
    [setPostItems],
  );

  return {
    actions: {
      changeUrl,
      remove,
    },
    add,
    clear,
    items: postItems,
    remainingSlots,
    urlRefs: postUrlRefs,
  };
}
